import { ref, computed, Ref } from 'vue';
import type { ImportStepConfig, ImportWizardState, FileSelectData } from './types';

// =============================================================================
// DEFAULT STEPS (for backward compatibility)
// =============================================================================

const DEFAULT_STEPS: ImportStepConfig[] = [
  {
    id: 'file_select',
    type: 'file_select' as const,
    descriptionKey: 'import.wizard.steps.select',
    allowCopyPaste: true,
  },
  {
    id: 'column_mapping',
    type: 'column_mapping' as const,
    descriptionKey: 'import.wizard.steps.map',
  },
  {
    id: 'preview',
    type: 'preview' as const,
    descriptionKey: 'import.wizard.steps.preview',
  },
];

// =============================================================================
// WIZARD STATE FACTORY
// =============================================================================

/**
 * Create initial wizard state
 */
export function createInitialWizardState(): ImportWizardState {
  return {
    currentStepIndex: 0,
    fileData: {
      file: null,
      pastedText: '',
      useCopyPaste: false,
    },
    parsedFile: null,
    mapping: [],
    selectedItems: [],
    availableItems: [],
    isSubmitting: false,
    consentAll: undefined,
    selectedVapidKeyId: undefined,
    selectedVapidKeyLabel: undefined,
  };
}

// =============================================================================
// USE IMPORT WIZARD COMPOSABLE
// =============================================================================

export interface UseImportWizardReturn {
  /** Current step configuration */
  currentStep: Ref<ImportStepConfig>;
  /** Current step index */
  currentStepIndex: Ref<number>;
  /** All steps configuration */
  steps: Ref<ImportStepConfig[]>;
  /** Wizard state */
  state: Ref<ImportWizardState>;
  /** Go to next step */
  next: () => void;
  /** Go to previous step */
  prev: () => void;
  /** Go to specific step by index */
  goToIndex: (index: number) => void;
  /** Go to specific step by id */
  goToStep: (stepId: string) => void;
  /** Check if can go to next step */
  canGoNext: Ref<boolean>;
  /** Check if can go to previous step */
  canGoBack: Ref<boolean>;
  /** Check if current step is the last one */
  isLastStep: Ref<boolean>;
  /** Check if current step is the first one */
  isFirstStep: Ref<boolean>;
  /** Reset wizard to initial state */
  reset: () => void;
  /** Update file data */
  updateFileData: (data: Partial<FileSelectData>) => void;
}

/**
 * Import wizard composable
 * Manages wizard navigation and state
 */
export default function useImportWizard(
  stepsConfig: ImportStepConfig[] = DEFAULT_STEPS,
): UseImportWizardReturn {
  // Steps configuration
  const steps = ref<ImportStepConfig[]>(stepsConfig);

  // Wizard state
  const state = ref<ImportWizardState>(createInitialWizardState());

  // Current step index (synced with state)
  const currentStepIndex = computed({
    get: () => state.value.currentStepIndex,
    set: (value: number) => {
      state.value.currentStepIndex = value;
    },
  });

  // Current step configuration
  const currentStep = computed(() => steps.value[currentStepIndex.value]);

  // Navigation helpers
  const canGoBack = computed(() => currentStepIndex.value > 0);
  const canGoNext = computed(() => currentStepIndex.value < steps.value.length - 1);
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);

  // Navigation methods
  const next = () => {
    if (canGoNext.value) {
      state.value.currentStepIndex += 1;
    }
  };

  const prev = () => {
    if (canGoBack.value) {
      state.value.currentStepIndex -= 1;
    }
  };

  const goToIndex = (index: number) => {
    if (index >= 0 && index < steps.value.length) {
      state.value.currentStepIndex = index;
    }
  };

  const goToStep = (stepId: string) => {
    const index = steps.value.findIndex((s) => s.id === stepId);
    if (index >= 0) {
      state.value.currentStepIndex = index;
    }
  };

  // Reset wizard
  const reset = () => {
    state.value = createInitialWizardState();
  };

  // Update file data helper
  const updateFileData = (data: Partial<FileSelectData>) => {
    state.value.fileData = { ...state.value.fileData, ...data };
  };

  return {
    currentStep,
    currentStepIndex,
    steps,
    state,
    next,
    prev,
    goToIndex,
    goToStep,
    canGoNext,
    canGoBack,
    isLastStep,
    isFirstStep,
    reset,
    updateFileData,
  };
}
